import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Box, Button, Checkbox, Typography } from '@material-ui/core';
import { getManagementInsights, getOpenJobPosts } from 'redux/slices/dashboard';
import { RootState } from 'redux/store';
import { PATH_DASHBOARD } from 'routes/paths';
import { deleteJobPost } from 'requests/vacancy';
import { getTranslatedList } from 'constants/constants';
import { getTexts } from 'constants/text/texts';
import useAuth from 'hooks/useAuth';
import useLocales from 'hooks/useLocales';

import { OpenJobPostCard } from 'components/CardJobPost';
import Page from 'components/containers/Page';
import PaginationContainer from 'components/containers/PaginationContainer';
import ListContainer from 'components/containers/ListContainer';
import PageContainer from 'components/containers/PageContainer';
import HeadingContainer from 'components/containers/HeadingContainer';
import {
  OverviewCheckBox,
  OverviewFilter,
  OverviewSearch
} from 'components/OverviewFilter';
import { resetJobPostsFilters, setJobPostsFilter } from 'redux/slices/filters';
import { ResetButton } from 'components/Buttons';
import {
  getCandidateAssessmentResults,
  getCandidateInfo,
  resetCandidate
} from 'redux/slices/candidate';
import useURLFilters from '@hooks/useURLFilters';
import DashboardNavbar from '../../layouts/dashboard/navbar/DashboardNavbar';
import {
  FilterGroupButton,
  FilterGroupStyle,
  OverviewSelectStyle
} from '../../styled';
import FileUploader from 'components/FileUploader';
import AIModal from 'components/AIModal';
import { UploadJobFileToAI } from 'functions';
import { resetDataFromJobFile } from 'redux/slices/jobSlice';

const JobPostsOverview = () => {
  const { t } = useLocales();
  const { id } = useParams<any>();
  const location = useLocation();
  const [paginationClick, setPaginationCLick] = useState<boolean>(false);
  const [checked, setChecked] = useState(false);
  const targetRef = useRef<HTMLDivElement>(null);

  const [showUploader, setShowUploader] = useState(true);
  const [openAIPopUp, setOpenAIPopUp] = useState(false);
  const {
    openJobPosts: { results, count },
    isJobPostLoading
  } = useSelector((state: RootState) => state.dashboard);
  const {
    page,
    status,
    owner,
    periodicity,
    limit,
    offset,
    query,
    salaries,
    degrees,
    locations,
    work_exps,
    customer
  } = useSelector((state: RootState) => state.filtersReducer.openJobPosts);
  const company: any = useSelector(
    (state: RootState) => state.generalRecruiter.company
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    user,
    isCandidate,
    isAdmin,
    isRecruiterPlus,
    isRecruiter,
    recruiterPermission,
    isDesigner
  } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const {
    PERIOD_TYPES,
    STATUS_OPTIONS,
    DEFAULT_MESSAGE_TEXTS,
    DEGREE_TYPES,
    SALARY_CHOICES,
    YEAR_CHOICES,
    SALARY_CHOICES_INR
  } = getTranslatedList();
  const {
    PERIODICITY,
    STATUS,
    DEGREE,
    SALARY_RANGE,
    LOCATION,
    CUSTOMERS,
    CAREER_LEVEL
  } = getTexts();

  const { mainInfo } = useSelector(
    (state: RootState) => state.candidate.candidate
  );

  // Add a separate selector for the assessment result
  const { result: assessmentCompetencies } = useSelector(
    (state: RootState) => state.candidate.assessmentResult
  );

  // Determine if we should show candidate comparison data
  const shouldShowCandidateComparison = isCandidate || !!id;

  useEffect(() => {
    dispatch(getManagementInsights());
    dispatch(resetDataFromJobFile());
  }, []);

  useEffect(() => {
    if (user?.id && !isDesigner) {
      dispatch(getCandidateAssessmentResults(user.id));
    }
  }, [dispatch, user?.id]);

  useEffect(() => {
    dispatch(setJobPostsFilter('offset', page * limit - limit));
  }, [dispatch, page, limit]);

  const onDelete = async (id: string | number) => {
    try {
      await deleteJobPost(id);
      enqueueSnackbar(DEFAULT_MESSAGE_TEXTS.deleted, { variant: 'success' });
    } catch {
      enqueueSnackbar(DEFAULT_MESSAGE_TEXTS.error, { variant: 'error' });
    }
    dispatch(
      getOpenJobPosts(
        customer?.id,
        isCandidate ? 'published' : status,
        periodicity,
        limit,
        offset,
        owner,
        query,
        salaries,
        degrees,
        locations,
        work_exps
      )
    );
  };

  const onEdit = (id: string | number) => {
    navigate(PATH_DASHBOARD.jobPosts.getJobPostUrl(id));
  };

  const orderFilter = () => {
    if (checked) {
      setChecked(!checked);
      dispatch(
        getOpenJobPosts(
          customer?.id,
          isCandidate ? 'published' : status,
          periodicity,
          limit,
          offset,
          owner,
          query,
          salaries,
          degrees,
          locations,
          work_exps,
          '',
          user.candidate_id
        )
      );
    } else {
      setChecked(!checked);
      dispatch(
        getOpenJobPosts(
          customer?.id,
          isCandidate ? 'published' : status,
          periodicity,
          limit,
          offset,
          owner,
          query,
          salaries,
          degrees,
          locations,
          work_exps,
          'score',
          user.candidate_id
        )
      );
      enqueueSnackbar(DEFAULT_MESSAGE_TEXTS.filtered, { variant: 'success' });
    }
  };

  // const onCreateNew = () =>
  //   navigate(PATH_DASHBOARD.jobPosts.getJobPostUrl('new'));

  const title = isCandidate ? t('Jobs') : t('Job Posts Overview');

  useEffect(() => {
    if (!isCandidate) {
      dispatch(
        getOpenJobPosts(
          customer?.id,
          status,
          periodicity,
          limit,
          offset,
          owner,
          query,
          salaries,
          degrees,
          locations,
          work_exps,
          id ? 'score' : '',
          id || null,
          id ? 50 : 0
        )
      );
    } else if (user?.candidate_id) {
      dispatch(
        getOpenJobPosts(
          '',
          'published',
          periodicity,
          limit,
          offset,
          owner,
          query,
          salaries,
          degrees,
          locations,
          work_exps,
          '',
          user.candidate_id
        )
      );
    }
  }, [
    dispatch,
    customer,
    status,
    periodicity,
    limit,
    offset,
    owner,
    query,
    salaries,
    degrees,
    locations,
    work_exps
  ]);

  useEffect(() => {
    if (isCandidate) {
      dispatch(getCandidateInfo(user?.candidate_id));
    } else if (id) {
      dispatch(getCandidateInfo(id));
    } else {
      // Reset candidate state when there's no candidate ID
      dispatch(resetCandidate());
    }
  }, [dispatch, id, isCandidate, user?.candidate_id]);

  useEffect(() => {
    if (paginationClick && targetRef.current && !isJobPostLoading) {
      targetRef.current.scrollIntoView({
        block: 'start'
      });
    }
  }, [results, isJobPostLoading, page, paginationClick]);

  useEffect(() => {
    if (isJobPostLoading === false) {
      setPaginationCLick(false);
    }
  }, [isJobPostLoading]);

  // Handle abort button for file parsing (no next steps)
  const handleAbortFileUpload = () => {
    setOpenAIPopUp(false);
    setShowUploader(true);
  };

  // Create Abort button for file parsing
  const createAbortButton = () => (
    <Button
      variant="outlined"
      onClick={handleAbortFileUpload}
      style={{
        marginTop: '16px',
        borderColor: '#ff6b6b',
        color: '#ff6b6b'
      }}
    >
      {t('Abort')}
    </Button>
  );

  const filterNames = [
    'limit',
    'offset',
    'status',
    'periodicity',
    'limit',
    'offset',
    'owner',
    'q',
    'salaries',
    'degrees',
    'locations',
    'work_exps'
  ];

  const currentFilters = useSelector(
    (state: RootState) => state.filtersReducer.openJobPosts
  );

  useURLFilters({
    fragmentIdentifier: '#all/',
    filterNames,
    setterFunction: setJobPostsFilter,
    currentFilters
  });

  const hasPublishedByMeFilter =
    isAdmin ||
    ((isRecruiterPlus || isRecruiter) &&
      (recruiterPermission === 'view_all' ||
        recruiterPermission === 'view_only_vacancies'));

  const handleUploadJobFile = (file: any) => {
    dispatch(resetDataFromJobFile());
    UploadJobFileToAI({
      file,
      path: '/dashboard/jobs/new',
      setShowUploader,
      setOpenAIPopUp,
      enqueueSnackbar,
      navigate,
      dispatch,
      translate: t
    });
  };

  // Check if we're on the direct route (bestMatches) vs hash fragment route (jobPostsOverview)
  // When rendered through Jobs component, the outer PageContainer already has padding
  // When rendered directly, we need to add the padding
  const isDirectRoute = location.pathname.includes('/jobs/overview/');
  const containerPadding = isDirectRoute ? '0 2vw' : 0;

  return (
    <Page title={title}>
      <PageContainer
        style={{
          padding: containerPadding
        }}
      >
        <DashboardNavbar title={title} />
        <HeadingContainer
          subtitle={t('Your job posts')}
          // to be added back in next PRs with updated endpoint
          // actionButton={
          //   showUploader &&
          //   (isAdmin || isRecruiter || isRecruiterPlus) && (
          //     <FileUploader
          //       type="file"
          //       variant="contained"
          //       onSubmit={handleUploadJobFile}
          //       buttonStyle={{ height: '100%', padding: '10px 45px' }}
          //       title={t('Please upload a file that contains the job description. We will attempt to extract the title, content, and competencies from the file.')}
          //     >
          //       {t('Create a new job based on a job file')}
          //     </FileUploader>
          //   )
          // }
        />
        <AIModal
          open={openAIPopUp}
          onClose={() => setOpenAIPopUp(false)}
          customMessage={t("Extracting the job title, description, and competencies from the file. This may take up to 1 minute. If you don't wish to wait, please click 'Abort' to stop the task.")}
          customButton={createAbortButton()}
          textStyle={{ fontSize: '16px', fontWeight: 400 }}
        />
        <FilterGroupStyle>
          <OverviewSelectStyle
            title={DEGREE}
            placeholder={t('Filter by degree')}
            activeOption={degrees}
            options={DEGREE_TYPES}
            setOption={(value: any) =>
              dispatch(setJobPostsFilter('degrees', value))
            }
          />
          <OverviewSelectStyle
            title={`${SALARY_RANGE} (${company.currency})`}
            placeholder={t(`Filter by salary range`)}
            activeOption={salaries}
            options={
              company.currency === 'INR' ? SALARY_CHOICES_INR : SALARY_CHOICES
            }
            setOption={(value: any) =>
              dispatch(setJobPostsFilter('salaries', value))
            }
          />
          {company.is_agency && (
            <OverviewSelectStyle
              title={CUSTOMERS}
              type="select-customers"
              placeholder={t('Filter by customer')}
              activeOption={customer}
              setOption={(value: any) =>
                dispatch(setJobPostsFilter('customer', value))
              }
              isPlaceholder={true}
            />
          )}
          <OverviewSelectStyle
            title={LOCATION}
            type="location"
            placeholder={t('Filter by location')}
            setOption={(value: any) =>
              dispatch(setJobPostsFilter('locations', value))
            }
            isPlaceholder={true}
          />
          <OverviewSelectStyle
            title={CAREER_LEVEL}
            placeholder={t('Filter by career level')}
            activeOption={work_exps}
            options={YEAR_CHOICES}
            setOption={(value: any) =>
              dispatch(setJobPostsFilter('work_exps', value))
            }
          />
          {hasPublishedByMeFilter && (
            <OverviewCheckBox
              title={t('Published by me')}
              activeOption={owner}
              options={{ on: 'me', off: 'all' }}
              setOption={(value) => dispatch(setJobPostsFilter('owner', value))}
            />
          )}
        </FilterGroupStyle>
        <FilterGroupButton
          style={{
            columnGap: '50px',
            alignItems: 'end'
          }}
        >
          <OverviewFilter
            title={PERIODICITY}
            activeOption={periodicity}
            options={PERIOD_TYPES}
            setOption={(value) =>
              dispatch(setJobPostsFilter('periodicity', value))
            }
          />

          {!isCandidate && (
            <OverviewFilter
              title={STATUS}
              activeOption={status}
              options={STATUS_OPTIONS}
              setOption={(value) =>
                dispatch(setJobPostsFilter('status', value))
              }
            />
          )}
          <OverviewSearch
            style={{
              minWidth: '270px'
            }}
            title={' '}
            placeholder={t('Search by Name')}
            setValue={useCallback(
              (value: any) => dispatch(setJobPostsFilter('query', value)),
              [dispatch]
            )}
            value={query}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              pb: 2,
              cursor: 'pointer'
            }}
            onClick={() => dispatch(resetJobPostsFilters())}
          >
            <Typography
              ref={targetRef}
              style={{
                fontFamily: 'Rubik',
                fontWeight: 700,
                fontSize: '18px',
                color: '#562C82'
              }}
            >
              {t('Reset All')}
            </Typography>
            <ResetButton />
          </Box>
          {isCandidate && (
            <Box sx={{ display: 'flex', alignItems: 'flex-end', pb: 2 }}>
              <span className="filter_label">Show best matches</span>
              <Checkbox
                checked={checked}
                onChange={orderFilter}
                color="primary"
              />
            </Box>
          )}
        </FilterGroupButton>
        <ListContainer
          loading={isJobPostLoading}
          list={results}
          card={OpenJobPostCard}
          getCardProps={(item: any) => ({
            editable: !isCandidate,
            onDelete: () => onDelete(item.id),
            onEdit: () => onEdit(item.id),
            fullWidth: true,
            showCompetencyComparison: shouldShowCandidateComparison,
            candidateCompetencies: (() => {
              if (isCandidate) {
                return assessmentCompetencies || {};
              }
              return mainInfo?.competencies || {};
            })()
          })}
          gridProps={{ md: 6, lg: 4 }}
        />
        <PaginationContainer
          page={page}
          count={count}
          perPage={limit}
          setPage={(value) => {
            setPaginationCLick(true);
            dispatch(setJobPostsFilter('page', value));
          }}
        />
      </PageContainer>
    </Page>
  );
};

export default JobPostsOverview;
